import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { colors } from '../../../constants/colors';

const BugReportsPieChart = ({ data = [], isLoading = false }) => {
  const chartData = data.map((item, index) => ({
    name: item.category || item.status || 'Unknown',
    value: item.count || 0,
    color: getCategoryColor(item.category || item.status, index)
  }));

  function getCategoryColor(category, index) {
    const colorMap = {
      'open': colors.status.error,
      'in_progress': colors.status.warning,
      'resolved': colors.status.success,
      'closed': colors.text.secondary,
      'UI/UX': '#8B5CF6',
      'Performance': '#F59E0B',
      'Functionality': '#EF4444',
      'Data': '#10B981',
      'Security': '#F97316',
      'Other': '#6B7280'
    };
    
    return colorMap[category] || colors.primary;
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = payload.reduce((sum, p) => sum + (p.value || 0), 0) || (data.payload?.total ?? 1);
      const pct = total ? ((data.value / total) * 100).toFixed(1) : '0';
      return (
        <div style={{
          backgroundColor: colors.background.primary,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ 
            margin: '0 0 4px 0', 
            fontWeight: '600', 
            color: colors.text.primary,
            fontSize: '14px'
          }}>
            {data.name}: {data.value}
          </p>
          <p style={{ 
            margin: 0, 
            color: colors.text.secondary,
            fontSize: '12px'
          }}>
            {pct}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }) => {
    return (
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '16px'
      }}>
        {payload.map((entry, index) => (
          <div key={index} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              backgroundColor: entry.color,
              borderRadius: '2px'
            }} />
            <span style={{
              fontSize: '12px',
              color: colors.text.secondary
            }}>
              {entry.value} ({entry.payload.value})
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '300px',
        color: colors.text.secondary
      }}>
        Loading...
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '300px',
        color: colors.text.secondary
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🐛</div>
        <p style={{ margin: 0, fontSize: '16px' }}>No bug reports available</p>
        <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>Bug reports will appear here when created</p>
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + (item.count || 0), 0);

  return (
    <div>
      <ResponsiveContainer width="100%" height={250}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={2}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      <CustomLegend payload={chartData.map(item => ({ ...item, value: item.name, payload: { ...item, total } }))} />
    </div>
  );
};

export default BugReportsPieChart;

